import IconButton from '@mui/material/IconButton'
import SvgIcon from '@mui/material/SvgIcon'
import Tooltip from '@mui/material/Tooltip'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useLazyGetAllWorkOrderNotesQuery } from 'api/notes/workOrderNotesCrud'
import { ReactComponent as WorkOrderNoteIcon } from 'assets/note/WorkOrderNote.svg'
import CombinedNotesDialog from 'components/notes/combined/CombinedNotesDialog'
import CombinedNotesPopover from 'components/notes/combined/CombinedNotesPopover'
import StyledBadge from 'components/notes/common/StyledBadge'
import ViewNotesPopover from 'components/notes/common/ViewNotesPopover'
import { NOTE_TYPES } from 'settings/constants/note'
import T from 'T'

const { WORK_ORDER } = NOTE_TYPES as Record<string, string>

const StyledBadgeUntyped = StyledBadge as React.ElementType
const ViewNotesPopoverUntyped = ViewNotesPopover as React.ElementType

// Scheduled, not immediate: the pointer has to reach the preview, whose truck icon has its own tooltip.
const CLOSE_DELAY_MS = 150

type WONotesBadgeProps = {
  workOrderId?: string
  notesCount?: number
  // `icon` lets callers swap the glyph (master-sequence cards) without losing the behavior.
  icon?: React.ReactNode
  // Either id makes this a Notes badge: extra tabs, the combined popover, and All rather than WO on open.
  locationId?: string
  configuredServiceId?: string
  // Keeps count, preview and opening tab on work order notes; the ids still add the other tabs.
  workOrderFirst?: boolean
}

const WONotesBadge = ({
  workOrderId,
  notesCount = 0,
  icon = null,
  locationId,
  configuredServiceId,
  workOrderFirst = false,
}: WONotesBadgeProps) => {
  const [viewWONotesPopoverAnchorEl, setViewWONotesPopoverAnchorEl] = useState<HTMLElement | null>(null)
  const [isOpenWONotesDialog, setIsOpenWONotesDialog] = useState(false)
  const hasNotes = useMemo(() => notesCount > 0, [notesCount])
  const isCombined = Boolean(locationId || configuredServiceId) && !workOrderFirst

  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const cancelScheduledClose = useCallback(() => clearTimeout(closeTimerRef.current), [])

  const handleViewWONotesPopoverOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      cancelScheduledClose()
      setViewWONotesPopoverAnchorEl(event.currentTarget)
    },
    [cancelScheduledClose]
  )
  const handleViewWONotesPopoverClose = useCallback(() => {
    cancelScheduledClose()
    setViewWONotesPopoverAnchorEl(null)
  }, [cancelScheduledClose])
  const handleScheduledClose = useCallback(() => {
    cancelScheduledClose()
    closeTimerRef.current = setTimeout(() => setViewWONotesPopoverAnchorEl(null), CLOSE_DELAY_MS)
  }, [cancelScheduledClose])

  useEffect(() => () => clearTimeout(closeTimerRef.current), [])

  const handleOpenWONotesDialog = useCallback(() => setIsOpenWONotesDialog(true), [])
  const handleCloseWONotesDialog = useCallback(() => setIsOpenWONotesDialog(false), [])

  return (
    <>
      <Tooltip
        arrow
        disableInteractive
        disableHoverListener={hasNotes}
        placement="bottom"
        title={isCombined ? T.NOTES : `${T.WORK_ORDER_NOTE}s`}
      >
        <IconButton
          size="small"
          onClick={handleOpenWONotesDialog}
          {...(hasNotes && {
            onMouseEnter: handleViewWONotesPopoverOpen,
            onMouseLeave: handleScheduledClose,
          })}
        >
          <StyledBadgeUntyped badgeContent={notesCount}>
            {icon ?? <SvgIcon inheritViewBox component={WorkOrderNoteIcon} />}
          </StyledBadgeUntyped>
        </IconButton>
      </Tooltip>
      {isCombined && viewWONotesPopoverAnchorEl && (
        <CombinedNotesPopover
          showNoteVisibilityOnMobileIcon
          anchorEl={viewWONotesPopoverAnchorEl}
          configuredServiceId={configuredServiceId}
          locationId={locationId}
          workOrderId={workOrderId}
          onClose={handleViewWONotesPopoverClose}
          onPaperMouseEnter={cancelScheduledClose}
          onPaperMouseLeave={handleScheduledClose}
        />
      )}
      {!isCombined && (
        <ViewNotesPopoverUntyped
          showNoteVisibilityOnMobileIcon
          anchorEl={viewWONotesPopoverAnchorEl}
          id={workOrderId}
          position="right"
          useLazyGetAllNotesQuery={useLazyGetAllWorkOrderNotesQuery}
          onClose={handleViewWONotesPopoverClose}
          onPaperMouseEnter={cancelScheduledClose}
          onPaperMouseLeave={handleScheduledClose}
        />
      )}
      {isOpenWONotesDialog && (
        <CombinedNotesDialog
          open
          configuredServiceId={configuredServiceId}
          locationId={locationId}
          workOrderId={workOrderId}
          onClose={handleCloseWONotesDialog}
          {...(!isCombined && { defaultTab: WORK_ORDER })}
        />
      )}
    </>
  )
}

export default WONotesBadge
